import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.guodongmedia.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: {
              '@type': 'Person',
              name: article.author,
              jobTitle: article.authorTitle,
            },
            datePublished: article.publishedAt,
            publisher: {
              '@type': 'Organization',
              name: '果冻传媒',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.guodongmedia.com/' },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.guodongmedia.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.guodongmedia.com/article/${article.id}` },
            ],
          }),
        }}
      />
      <main>
        <article className="pt-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-300">·</span>
                <span className="text-xs text-foreground-400">{article.publishedAt}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-sm">{article.author.charAt(0)}</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                  <div className="text-xs text-foreground-500">{article.authorTitle}</div>
                </div>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full object-cover object-top"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center flex-wrap gap-2 mt-10 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关文章</h2>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={rel.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span>{rel.author}</span>
                        <span>·</span>
                        <span>{rel.publishedAt}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}